import React from "react";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";

const RALLY_IMG =
  "https://images.unsplash.com/photo-1604325370657-ac472c852171?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzZ8MHwxfHNlYXJjaHwzfHxpbmRpYW4lMjBwZW9wbGUlMjBjcm93ZCUyMHJhbGx5fGVufDB8fHxibGFja19hbmRfd2hpdGV8MTc4MDgwNDUxM3ww&ixlib=rb-4.1.0&q=85";

const services = [
  {
    n: "01",
    title: "Political Election Campaigns",
    sub: "Assembly · Local Body · National",
    body: "Constituency-level intelligence, candidate positioning, rally choreography and 360° narrative control across the full election cycle.",
    tall: true,
  },
  {
    n: "02",
    title: "Data Analysis & War Room",
    sub: "Booth-level analytics",
    body: "Sentiment, demographic and turnout modelling. Daily dashboards that translate ground reality into the next move.",
  },
  {
    n: "03",
    title: "Online & Offline PR",
    sub: "Integrated Distribution",
    body: "Print, broadcast, digital, on-ground. One story, every surface, perfectly synchronised.",
  },
  {
    n: "04",
    title: "Page Handling & Social",
    sub: "Always-on Operations",
    body: "Page strategy, content production, community moderation and rapid response for political and corporate handles.",
  },
  {
    n: "05",
    title: "Corporate & Institutional PR",
    sub: "Reputation Architecture",
    body: "Brand narrative, leadership profiling, media relations and stakeholder communications for jewellery houses, colleges, banks, institutions and coaching centres.",
  },
  {
    n: "06",
    title: "Full-Service Marketing & Events",
    sub: "Integrated Growth Engine",
    body: "End-to-end marketing stack — branding, creative, digital, events production and corporate dispute management, now extending into international logistics & shipment.",
    wide: true,
  },
];

const Services = () => {
  return (
    <section
      id="services"
      data-testid="services-section"
      className="section-gutter py-20 md:py-32 border-t border-black/10"
    >
      <div className="grid grid-cols-12 gap-6 mb-12 md:mb-20">
        <div className="col-span-12 md:col-span-6">
          <div className="overline text-black/60">Chapter 02 — Practice</div>
          <h2 className="font-serif-display text-5xl md:text-6xl lg:text-7xl mt-6 leading-[0.92]">
            Six disciplines. <em>One command room.</em>
          </h2>
        </div>
        <div className="col-span-12 md:col-span-5 md:col-start-8 self-end">
          <p className="text-base md:text-lg text-black/70 leading-relaxed">
            From booth-level mobilisation to boardroom narrative — we operate at every scale where
            perception decides outcome.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-12 gap-4 md:gap-6 auto-rows-auto">
        {/* Tall card #1 + image */}
        {services.map((s, i) => {
          const span = s.tall
            ? "md:col-span-7 md:row-span-2"
            : s.wide
            ? "md:col-span-8"
            : "md:col-span-5";
          return (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.6, delay: 0.06 * i, ease: [0.22, 1, 0.36, 1] }}
              className={`col-span-12 ${span} svc-card p-6 md:p-8 flex flex-col justify-between min-h-[260px] md:min-h-[300px]`}
              data-testid={`service-card-${i}`}
            >
              <div className="flex items-start justify-between">
                <span className="svc-num text-2xl">{s.n}</span>
                <ArrowUpRight size={20} className="opacity-60" />
              </div>

              {s.tall && (
                <div className="my-6 -mx-6 md:-mx-8 border-y border-black/10">
                  <img
                    src={RALLY_IMG}
                    alt="Political rally crowd"
                    className="w-full h-56 md:h-72 object-cover bw"
                    loading="lazy"
                  />
                </div>
              )}

              <div>
                <div className="overline opacity-70">{s.sub}</div>
                <h3 className="font-serif-display text-3xl md:text-4xl mt-3 leading-[1.02]">
                  {s.title}
                </h3>
                <p className="mt-4 text-sm md:text-base leading-relaxed opacity-80 max-w-md">
                  {s.body}
                </p>
              </div>
            </motion.div>
          );
        })}
      </div>
    </section>
  );
};

export default Services;
